import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Box, Grid } from '@mui/material';
import { Button, Dialog, FormikInputPhone, FormikInputSelect, FormikInputString, H1, Notification, P1R, P2 } from 'src/components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectCurrTenantId,
  selectInviteTenantUserSuccess,
  selectInviteTenantUserLoading,
  selectInviteTenantUserErrors,
  selectInviteStorm360AdminSuccess,
  selectInviteSharedUserLoading,
  selectInviteSharedUserSuccess,
  selectInviteSharedUserErrors,
  selectInviteStorm360AdminErrors,
} from 'src/redux/tenants/selectors';
import { inviteSharedUser, inviteStorm360Admin, inviteTenantUser } from 'src/redux/tenants/actions';
import { useEffect, useState } from 'react';
import { themeColors } from 'src/theme';
import config from 'src/config';
import { ListType, Role } from 'src/types';
import { RoleType } from 'src/shared/enums/roleType.enum';
import { SharedRoleSlug } from 'src/shared/enums/roleSlug.enum';
import { VALIDATOR } from 'src/utils/validationSchemes';

type InviteUserDialogProps = {
  theme: 'manager' | 'user';
  setInviteInfo: ({ email, role }: { email: string; role: string }) => void;
  onClose: () => void;
};

export type FormValues = {
  email: string;
  phone: string;
  firstName: string;
  lastName: string;
  role?: string;
};

const InviteUserDialog = ({ theme, setInviteInfo, onClose }: InviteUserDialogProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [inviteError, setInviteError] = useState<string | null>(null);
  const currTenantId = useSelector(selectCurrTenantId);
  const isInvitingUser = useSelector(selectInviteTenantUserLoading);
  const isInvitingUserSuccess = useSelector(selectInviteTenantUserSuccess);
  const isInvitingSharedUser = useSelector(selectInviteSharedUserLoading);
  const isInvitingSharedUserSuccess = useSelector(selectInviteSharedUserSuccess);
  const isInvitingStorm360AdminSuccess = useSelector(selectInviteStorm360AdminSuccess);
  const inviteErrors = useSelector(selectInviteTenantUserErrors) as Record<string, string[]>;
  const inviteSharedErrors = useSelector(selectInviteSharedUserErrors) as Record<string, string[]>;
  const inviteStorm360Errors = useSelector(selectInviteStorm360AdminErrors) as Record<string, string[]>;

  const initialValues: FormValues = {
    email: '',
    phone: '',
    firstName: '',
    lastName: '',
    role: '',
  };

  let roles: ListType[] = [];
  if (theme === 'user') {
    roles = config.options.roles
      .filter(
        (role: Role) =>
          (role.types.includes(RoleType.FIELD_WORKER) || role.types.includes(RoleType.SUPPORT)) &&
          !role.types.includes(RoleType.HIDDEN) &&
          !role.types.includes(RoleType.SUPER_ADMIN),
      )
      .map((role: Role) => ({ key: role.slug, value: role.name }));
  } else if (theme === 'manager') {
    roles = config.options.roles
      .filter(
        (role: Role) =>
          (role.types.includes(RoleType.SYSTEM_ADMIN) || role.types.includes(RoleType.COMPANY_ADMIN)) && role.types.includes(RoleType.HIDDEN),
      )
      .map((role: Role) => ({ key: role.slug, value: role.name }));
  }

  const validationSchema = Yup.object({
    email: VALIDATOR.email,
    phone: VALIDATOR.phone,
    firstName: VALIDATOR.name.notRequired(),
    lastName: VALIDATOR.name.notRequired(),
    role: theme === 'user' ? VALIDATOR.role : VALIDATOR.role.notRequired(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (data) => {
      if (theme === 'manager') {
        dispatch(
          inviteStorm360Admin.init({
            admin: {
              ...data,
              phone: data.phone || null,
              role: undefined,
              firstName: data.firstName?.trim() || null,
              lastName: data.lastName?.trim() || null,
            },
          }),
        );
      } else if (theme === 'user') {
        if (data.role && data.role in SharedRoleSlug) {
          dispatch(
            inviteSharedUser.init({
              id: currTenantId,
              user: { ...data, phone: data.phone ? data.phone : null, firstName: undefined, lastName: undefined },
            }),
          );
        } else {
          dispatch(
            inviteTenantUser.init({
              id: currTenantId,
              user: { ...data, phone: data.phone ? data.phone : null, firstName: undefined, lastName: undefined },
            }),
          );
        }
      }

      setInviteInfo({
        email: data.email,
        role: data.role || 'SYSTEM_MANAGER',
      });
    },
  });

  const onCloseNotification = () => {
    setInviteError(null);
    dispatch(inviteTenantUser.reset());
    dispatch(inviteSharedUser.reset());
    dispatch(inviteStorm360Admin.reset());
  };

  const handleClose = () => {
    formik.resetForm();
    onClose();
    onCloseNotification();
  };

  useEffect(() => {
    dispatch(inviteTenantUser.reset());
    dispatch(inviteSharedUser.reset());
    dispatch(inviteStorm360Admin.reset());
  }, []);

  useEffect(() => {
    if (isInvitingUserSuccess || isInvitingStorm360AdminSuccess || isInvitingSharedUserSuccess) {
      handleClose();
      formik.resetForm();
    }
  }, [isInvitingUserSuccess, isInvitingStorm360AdminSuccess, isInvitingSharedUserSuccess]);

  useEffect(() => {
    if (inviteErrors && typeof inviteErrors === 'string') {
      setInviteError(inviteErrors);
    }
    if (inviteSharedErrors && typeof inviteSharedErrors === 'string') {
      setInviteError(inviteSharedErrors);
    }
  }, [inviteErrors, inviteSharedErrors]);

  return (
    <>
      <Dialog open aria-labelledby="customized-dialog-title" onClose={handleClose}>
        <Box sx={{ gap: '32px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <H1>
            {theme === 'manager' && t('storm360UsersPage.inviteSystemManager')}
            {theme === 'user' && t('usersPage.inviteUser')}
          </H1>
          <P1R sx={{ lineHeight: '18.2px', textAlign: 'center', whiteSpace: 'pre-line' }}>
            {theme === 'manager' && t('storm360UsersPage.inviteSystemManagerSubTitle')}
            {theme === 'user' && t('usersPage.inviteUserSubTitle')}
          </P1R>
        </Box>

        <Grid container sx={{ margin: '32px 0 16px 0', display: 'block' }}>
          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputString
                isRequired
                formik={formik}
                field="email"
                label={t('companyPage.emailAdress')}
                errorHelper={inviteErrors || inviteSharedErrors || inviteStorm360Errors}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: '100%' }}>
              <FormikInputPhone isRequired formik={formik} field="phone" label={t('companyPage.phone')} />
            </Box>
          </Grid>

          {theme === 'manager' && (
            <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <FormikInputString formik={formik} field="firstName" label={t('companyPage.firstName')} errorHelper={inviteErrors} />
              </Box>
            </Grid>
          )}

          {theme === 'manager' && (
            <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <FormikInputString formik={formik} field="lastName" label={t('companyPage.lastName')} errorHelper={inviteErrors} />
              </Box>
            </Grid>
          )}

          {theme === 'user' && (
            <Grid item xs={12} sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <Box sx={{ width: '100%' }}>
                <FormikInputSelect isRequired formik={formik} field="role" label="Select Role" options={roles} disableClearable />
              </Box>
            </Grid>
          )}
        </Grid>

        <Grid container columnSpacing={2} sx={{ mb: 5 }}>
          <Grid item xs={6}>
            <Button primary={false} onClick={onClose} sx={{ width: '100%', textTransform: 'capitalize' }}>
              {t('common.cancel')}
            </Button>
          </Grid>

          <Grid item xs={6}>
            <Button
              onClick={() => formik.handleSubmit()}
              loading={isInvitingUser || isInvitingSharedUser}
              sx={{
                width: '100%',
                textTransform: 'capitalize',
              }}
            >
              {t('companyPage.sendInvite')}
            </Button>
          </Grid>
        </Grid>
      </Dialog>

      <Notification severity="error" open={Boolean(inviteError)} onClose={onCloseNotification}>
        <P2 sx={{ color: themeColors.white, textTransform: 'none' }}>{inviteError}</P2>
      </Notification>
    </>
  );
};

export default InviteUserDialog;
